import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import PTAFormField from 'Components/PTAFormField/PTAFormField'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import { isAuthorityAdmin, isSuperuser } from 'Services/common/Features/User/userSlice'
import {
  useCreateSingleTariffMutation,
  useUpdateSingleTariffMutation
} from 'Services/ProductManagement/Features/Tariff/tariffApi'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import { TariffStatus } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetVatDefinitionsQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { useTypedSelector } from 'Store'
import type { ProductTariffError } from 'Util/ApiClient'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  TARIFF_CODE_LENGTH
} from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select
} from '@mui/material'
import type React from 'react'
import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: Record<string, string[]> = {
  tariff_code: [],
  description: [],
  valid_from: [],
  valid_until: [],
  status: [],
  pre_order_start_timestamp: []
}

interface TariffFormProps {
  tariff?: Tariff
  successBaseUrl: string
}

const generateTariffValidityTitle = () => {
  return `tariff-validity-title`
}

export default function TariffForm({ tariff, successBaseUrl }: Readonly<TariffFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!tariff
  const { user } = useTypedSelector(state => state.user)

  const [createSingleTariff, createStatus] = useCreateSingleTariffMutation()
  const [updateSingleTariff, updateStatus] = useUpdateSingleTariffMutation()
  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [tariffDetails, setTariffDetails] = useState<Partial<Tariff>>(isEdit ? tariff : {})

  const tariffValidity = tariffDetails?.active_period ?? null

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  const { data: vatDefinitions = [], isFetching: isVatDefinitionsFetching } =
    useGetVatDefinitionsQuery()

  useEffect(() => {
    if (isEdit && tariff) {
      setTariffDetails(tariff)
    }
  }, [tariff, isEdit])

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Tariff
  ) => {
    if (value !== undefined) {
      setTariffDetails(prevState => ({ ...prevState, [field]: value }) as Tariff)
    }
  }

  const handleValidityDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(
      prevState =>
        ({
          ...prevState,
          active_period: {
            ...(prevState.active_period ?? null),
            [name]: formattedValue,
            title: generateTariffValidityTitle()
          }
        }) as Tariff
    )
  }

  const handlePreOrderStartTimestampChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(prevState => ({ ...prevState, [name]: formattedValue }) as Tariff)
  }

  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    setTariffDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const tariffData = {
      ...tariffDetails,
      product_ids: tariffDetails.product_ids ?? [],
      parent_product_ids: tariffDetails.parent_product_ids ?? []
    }

    const mutation = isEdit
      ? updateSingleTariff({
          ...tariffData,
          id: tariff.id
        })
      : createSingleTariff({
          ...tariffData,
          pta: IS_HSL_SERVICE_ENVIRONMENT || isAuthorityAdmin(user) ? user?.pta : tariffData.pta
        })

    mutation
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Tariff successfully saved.`)
        navigate(successBaseUrl)
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the tariff.`)
      })
  }

  const tariffStatusOptions = [
    {
      label: t`Active`,
      value: TariffStatus.ACTIVE
    },
    {
      label: t`Passive`,
      value: TariffStatus.PASSIVE
    },
    {
      label: t`Draft`,
      value: TariffStatus.DRAFT
    },
    {
      label: t`Production ready`,
      value: TariffStatus.STAGED
    },
    {
      label: t`Pre-sale`,
      value: TariffStatus.ACTIVE_PRE_ORDER
    }
  ]

  const tariffStatusLabelId = useId()

  return (
    <WithLoader loading={isVatDefinitionsFetching}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && isSuperuser(user) && (
              <Box>
                <PTAFormField
                  value={tariffDetails.pta}
                  onChange={value => handleChange(value, 'pta')}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormTextField
                name="tariff_code"
                required
                label={t`Tariff code`}
                value={tariffDetails?.tariff_code}
                slotProps={{
                  htmlInput: {
                    minLength: TARIFF_CODE_LENGTH,
                    maxLength: TARIFF_CODE_LENGTH
                  }
                }}
                onChange={e => handleChange(e.target.value, 'tariff_code')}
                {...getErrorProps('tariff_code')}
              />
              <FormTextField
                name="description"
                required
                label={t`Tariff description`}
                sx={{ minWidth: 280 }}
                value={tariffDetails?.description}
                onChange={e => handleChange(e.target.value, 'description')}
                {...getErrorProps('description')}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CustomDateTimePicker
                name="valid_from"
                value={tariffValidity?.valid_from ? new Date(tariffValidity.valid_from) : null}
                dateToCompare={
                  tariffValidity?.valid_until ? new Date(tariffValidity.valid_until) : null
                }
                dateToCompareFieldName="valid_until"
                isStartDate={true}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active from`}
                isError={fieldErrors.valid_from?.length > 0}
                isRequired={true}
                helperText={getFieldErrorHelperText('valid_from')}
              />
              <Box>{` - `}</Box>
              <CustomDateTimePicker
                name="valid_until"
                value={tariffValidity?.valid_until ? new Date(tariffValidity.valid_until) : null}
                dateToCompare={
                  tariffValidity?.valid_from ? new Date(tariffValidity.valid_from) : null
                }
                dateToCompareFieldName="valid_from"
                isStartDate={false}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active to`}
                isError={fieldErrors.valid_until?.length > 0}
                isRequired={true}
                helperText={getFieldErrorHelperText('valid_until')}
              />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <CustomDateTimePicker
                name="pre_order_start_timestamp"
                value={
                  tariffDetails?.pre_order_start_timestamp
                    ? new Date(tariffDetails.pre_order_start_timestamp)
                    : null
                }
                onChange={handlePreOrderStartTimestampChange}
                label={t`Pre-order start time`}
                isError={fieldErrors.pre_order_start_timestamp.length > 0}
                helperText={getFieldErrorHelperText('pre_order_start_timestamp')}
              />
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 200 }} size="small" required>
                <InputLabel size="small">{t`VAT definition`}</InputLabel>
                <Select
                  name="vat_definition_id"
                  value={tariffDetails.vat_definition_id ?? ''}
                  label={t`VAT definition`}
                  size="small"
                  onChange={e => handleChange(e.target.value, 'vat_definition_id')}
                >
                  {vatDefinitions.map(vatDefinition => (
                    <MenuItem key={vatDefinition.id} value={vatDefinition.id}>
                      {vatDefinition.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl required error={fieldErrors.status.length > 0}>
                <StyledLabel id={tariffStatusLabelId}>{t`Tariff status`}</StyledLabel>
                <RadioGroup
                  value={tariffDetails.status}
                  onChange={handleRadioButtonChange}
                  name="status"
                  aria-labelledby={tariffStatusLabelId}
                >
                  {tariffStatusOptions.map(({ label, value }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" required />}
                      label={label}
                    />
                  ))}
                </RadioGroup>
                <FormHelperText error={fieldErrors.status?.length > 0}>
                  {getFieldErrorHelperText('status')}
                </FormHelperText>
              </FormControl>
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save tariff`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
